/**
 * @fileoverview The org portal's Settings area: which sections it holds, in the
 * order the hub lists them, and which section a path belongs to.
 *
 * Plan, Admins, Branding, Features and Activity used to be five sidebar items
 * of their own, beside the pages an admin opens every day. They are things an
 * admin sets once and revisits rarely, so they share one Settings item pinned
 * to the bottom of the rail (where the instructor portal keeps its own) and a
 * hub at /org/settings. Each section keeps a page of its own under it.
 *
 * Schedule — the Kalver Scheduler connection and its connected classes —
 * joined them when the Dashboard became the reporting page: it is set up
 * once and then read approximately never, which is exactly what this hub is
 * for. The Calendar page's "Scheduling preferences" button opens it directly.
 *
 * Since iOS review C10 the hub opens on You (your account and appearance,
 * which were sections of the Admins page), then the Organization, Scheduling
 * and History. Notifications joined the Organization from the rail, where it
 * sat beside the pages an admin opens every day.
 *
 * `legacyPath` is where the section lived before. Those paths still redirect,
 * query string included, because a bookmark may name them and Stripe's
 * checkout returns to `/org/subscription?checkout=…`. A section that never
 * had a page of its own has none.
 */

export const ORG_SETTINGS_PATH = "/org/settings";

/** Where the Calendar's "Scheduling preferences" button, and every other link
    to the Scheduler connection, goes. */
export const ORG_SCHEDULE_SETTINGS_PATH = "/org/settings/schedule";

export const ORG_SETTINGS_SECTIONS = [
  // You: what is the signed-in admin's alone (iOS review C10).
  {
    id: "account",
    group: "you",
    label: "Preferences",
    icon: "user",
    path: "/org/settings/account",
    desc: "The emails you receive, your password, and deleting your account",
  },
  {
    id: "appearance",
    group: "you",
    label: "Appearance",
    icon: "sunMoon",
    path: "/org/settings/appearance",
    desc: "Light or dark, on this device",
  },
  // The organization.
  {
    id: "plan",
    group: "organization",
    label: "Plan",
    icon: "creditCard",
    path: "/org/settings/plan",
    legacyPath: "/org/subscription",
    desc: "Your subscription, add-ons and what you're billed for",
  },
  {
    id: "admins",
    group: "organization",
    label: "Admins",
    icon: "shield",
    path: "/org/settings/admins",
    legacyPath: "/org/admins",
    desc: "Who can manage this portal",
  },
  {
    id: "branding",
    group: "organization",
    label: "Branding",
    icon: "sparkle",
    path: "/org/settings/branding",
    legacyPath: "/org/branding",
    desc: "Logo, display name, accent colour and your own web address",
  },
  {
    id: "features",
    group: "organization",
    label: "Features",
    icon: "sliders",
    path: "/org/settings/features",
    legacyPath: "/org/features",
    desc: "Which Hans features your instructors have, and the default for substitute requests",
  },
  {
    id: "notifications",
    group: "organization",
    label: "Notifications",
    icon: "bell",
    path: "/org/settings/notifications",
    legacyPath: "/org/notifications",
    desc: "What your instructors are sent, and when",
  },
  {
    id: "schedule",
    group: "scheduling",
    label: "Schedule",
    icon: "calendar",
    path: "/org/settings/schedule",
    desc: "Your Kalver Scheduler connection, connected classes and booking consent",
  },
  {
    id: "activity",
    group: "history",
    label: "Activity",
    icon: "clock",
    path: "/org/settings/activity",
    legacyPath: "/org/activity",
    desc: "Every administrative change, permanently recorded",
  },
];

/** The hub's groups, in order (iOS review C10): You apart from the organization. */
export const ORG_SETTINGS_GROUPS = [
  { id: "you", label: "You" },
  { id: "organization", label: "Organization" },
  { id: "scheduling", label: "Scheduling" },
  { id: "history", label: "History" },
];

/** The sections under each group, in order, leaving out a group with none. */
export function orgSettingsGroups() {
  return ORG_SETTINGS_GROUPS
    .map((group) => ({ ...group, sections: ORG_SETTINGS_SECTIONS.filter((s) => s.group === group.id) }))
    .filter((group) => group.sections.length > 0);
}

/** The section a path is inside, or null for the hub and everything else. */
export function settingsSectionFor(pathname) {
  return ORG_SETTINGS_SECTIONS.find((s) => pathname === s.path || pathname.startsWith(`${s.path}/`)) || null;
}
